import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import Header from '../../components/Header';
import Loader from '../../components/Loader';
import ModeCard from './components/ModeCard';

import useSavedWithdrawalModes from '../../api/useSavedWithdrawalModes';
import { NAVIGATION_ROUTES, CLEVERTAP_EVENTS } from '../../constant';
import { pushClevertapEvent } from '../../utils/analytics/clevertap';
import type { WithdrawMode } from '../../interfaces/Withdrawal';
import styles from './styles.module.css';

/**
 * Saved withdrawal modes — view and change.
 *
 * Mirrors Android's `SavedWithdrawalModesFragment`, which has no delete path:
 * modes are added or changed, never removed from here. Delinking is something
 * the backend does and reports, not a user action.
 */
const WithdrawModes: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { savedModes, isLoading, error, refetch } = useSavedWithdrawalModes();

  useEffect(() => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WITHDRAW_SAVED_MODES_SCREEN);
  }, []);

  /**
   * Both add and change land on the add-mode form, scoped to the one mode so
   * its picker is skipped.
   *
   * `isEdit` is passed explicitly because this screen has just fetched the
   * linked state from the server, which is more reliable than the user store —
   * and it decides between ADD_* and EDIT_*, which the backend validates.
   */
  const openModeForm = (mode: WithdrawMode, isEdit: boolean) => {
    pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_WITHDRAW_CHANGE_MODE_CLICKED, {
      mode,
      isEdit,
    });
    navigate(NAVIGATION_ROUTES.WITHDRAW_ADD_MODE, {
      state: { offeredModes: [mode], isEdit },
    });
  };

  if (isLoading) return <Loader />;

  const hasAnyMode = Boolean(savedModes?.vpa || savedModes?.bank);

  return (
    <div className={styles.modesPage}>
      <Header
        pageTitle={t('savedModesTitle')}
        routeValue={NAVIGATION_ROUTES.WITHDRAW}
      />

      <div className={styles.modesBody}>
        {error || !hasAnyMode ? (
          <div className={styles.errorState}>
            <p className="fontSizeSm14">
              {(error as Error)?.message || t('noWithdrawModeText')}
            </p>
            <button
              type="button"
              className="btn primaryBtn mdHeight"
              onClick={() => refetch()}
            >
              {t('retryLabel')}
            </button>
          </div>
        ) : (
          <>
            {/* UPI first, matching Android's ordering on this screen. */}
            <ModeCard
              mode="UPI"
              data={savedModes?.vpa}
              onAdd={(mode) => openModeForm(mode, false)}
              onChange={(mode) => openModeForm(mode, true)}
              onBlocked={(message) => toast.info(message)}
            />
            <ModeCard
              mode="BANK"
              data={savedModes?.bank}
              onAdd={(mode) => openModeForm(mode, false)}
              onChange={(mode) => openModeForm(mode, true)}
              onBlocked={(message) => toast.info(message)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default WithdrawModes;
